import { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { GrButton, GrCheckbox, GrIconButton, GrMenu, GrMenuItem, GrStack, GrTextField, GrTypography, palette } from "@jnpmedi/grebob";
import classNames from "classnames";
import { MvnMessage } from "@newLibs/utils";
import { FilterChip } from "./FilterChip";
import {
  convertStringToFilterValues,
  FilterCriteria,
  FilterOption,
  FilterType,
  FilterValue,
  FilterValues,
  transformFiltersToQueryParams,
} from "./types";
import { useTableFilters } from "../hooks/useTableFilters";
import { DOCS_COMMON_LANG } from "@maven-msg/lang/docs/common";

export type DocsTableFilterGroupProps<T> = {
  filters: FilterOption<T>[];
  className?: string;
  style?: CSSProperties;
};

/**
 * @param filters - 필터 옵션 배열
 * @description Generic T에는 maven-rest에 선언된 서버에 요청하는 request type을 명시
 */
export function DocsTableFilterGroup<T extends Record<string, any>>({ filters, className, style }: DocsTableFilterGroupProps<T>) {
  const { tableFilters, updateTableFilters, resetTableFilters } = useTableFilters();

  const [filterValues, setFilterValues] = useState<Record<keyof T, FilterCriteria>>({} as Record<keyof T, FilterCriteria>);
  const [editingTextValues, setEditingTextValues] = useState<Record<string, FilterValue>>({});
  const [contextMenu, setContextMenu] = useState<{ filter: FilterOption<T>; anchor: HTMLElement } | null>(null);

  useEffect(() => {
    const result: Record<keyof T, FilterCriteria> = {} as Record<keyof T, FilterCriteria>;

    for (const filter of filters) {
      let initialValues: FilterValue[] = [];

      const filterValue = tableFilters[filter.key];

      if (filterValue) {
        if (filter.type === FilterType.Select && typeof filterValue === "string") {
          initialValues = convertStringToFilterValues(filterValue, filter.options || []);
        } else if (filter.type === FilterType.Text) {
          initialValues = [{ value: filterValue as string, label: filterValue as string }];
        }
      }

      result[filter.key] = {
        type: filter.type,
        values: initialValues,
      };
    }

    setFilterValues(result);
  }, [filters, tableFilters]);

  const hasActiveFilter = useMemo(() => {
    return filters.some((filter) => {
      const filterValue = filterValues[filter.key]?.values;
      return filterValue && filterValue.length > 0;
    });
  }, [filters, filterValues]);

  const handleFilterChange = useCallback(
    (key: keyof T & string, value: FilterValues) => {
      setFilterValues((prevFilters) => {
        const updatedFilters = {
          ...prevFilters,
          [key]: { ...prevFilters[key], values: value },
        };

        const transformedFilters = transformFiltersToQueryParams(updatedFilters);

        updateTableFilters(transformedFilters);

        return updatedFilters;
      });
    },
    [updateTableFilters]
  );

  const handleTextValueChange = useCallback(
    (key: keyof T & string, newValue: string) => {
      const filterLabel = filters.find((f) => f.key === key)?.label || "";
      setEditingTextValues((prev) => ({
        ...prev,
        [key]: { label: filterLabel, value: newValue },
      }));
    },
    [filters]
  );

  const handleClearFilter = useCallback(
    (key: keyof T & string) => {
      handleTextValueChange(key, "");
      handleFilterChange(key, []);
    },
    [handleFilterChange, handleTextValueChange]
  );

  const handleSubmitText = useCallback(
    (key: keyof T & string) => {
      if (editingTextValues[key]?.value) {
        const filterLabel = filters.find((filter) => filter.key === key)?.label || "";
        handleFilterChange(key, [{ label: filterLabel, value: editingTextValues[key].value }]);
      }
    },
    [editingTextValues, filters, handleFilterChange]
  );

  const handleResetFilters = useCallback(() => {
    const transformedFilters = transformFiltersToQueryParams(filterValues);

    resetTableFilters(transformedFilters);

    const resetValues: Record<keyof T, FilterCriteria> = {} as Record<keyof T, FilterCriteria>;

    for (const filter of filters) {
      resetValues[filter.key] = {
        type: filter.type,
        values: [],
      };
    }

    setFilterValues(resetValues);
    setEditingTextValues({});
  }, [resetTableFilters, filterValues, filters, setFilterValues, setEditingTextValues]);

  const renderTextField = useMemo(
    () => (filter: FilterOption<T>) => {
      const key = filter.key;
      const currentValue = editingTextValues[key]?.value || "";
      const hasValue = !!editingTextValues[key]?.value?.length;

      return (
        <StyledTextField
          style={{ width: "220px" }}
          value={currentValue}
          size="medium"
          endAdornment={
            hasValue && (
              <GrStack
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleClearFilter(key);
                }}
              >
                <GrIconButton iconType="circle-x-filled" size={16} />
              </GrStack>
            )
          }
          placeholder={MvnMessage(DOCS_COMMON_LANG.ENTER_VALUE_PLACEHOLDER)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleTextValueChange(key, e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmitText(key)}
          onBlur={() => {
            setContextMenu(null);
            handleSubmitText(key);
          }}
        />
      );
    },
    [editingTextValues, handleClearFilter, handleSubmitText, handleTextValueChange]
  );

  const renderSelectOptions = useMemo(
    () => (filter: FilterOption<T>) => {
      const key = filter.key;
      const selectedValues = filterValues[key].values;

      if (!filter.options) {
        return [];
      }

      return filter.options.map(({ label, value }) => {
        const isSelected = selectedValues.some((item) => item.value === value);

        return {
          type: "item" as const,
          children: (
            <GrStack onClick={(e) => e.stopPropagation()} width="100%" minWidth={150} padding="2px 8px 2px 4px">
              <GrCheckbox
                id={value}
                label={{ text: label, htmlFor: value }}
                checked={isSelected}
                onChange={() => {
                  const updatedValues = isSelected
                    ? selectedValues.filter((item) => item.value !== value)
                    : [...selectedValues, { label, value }];
                  handleFilterChange(key, updatedValues);
                }}
              />
            </GrStack>
          ),
        };
      });
    },
    [filterValues, handleFilterChange]
  );

  const renderContextMenuItems = useMemo(
    () =>
      (filter: FilterOption<T>): GrMenuItem[] => {
        if (filter.type === FilterType.Text) {
          return [{ type: "item", children: renderTextField(filter) }];
        }

        if (filter.type === FilterType.Select) {
          return renderSelectOptions(filter);
        }

        return [];
      },
    [renderTextField, renderSelectOptions]
  );

  const handleChangeContextMenu = useCallback((menu: { filter: FilterOption<T>; anchor: HTMLElement }) => {
    setContextMenu(menu);
  }, []);

  return (
    <DocsTableFilterGroupWrapper style={style} className={classNames("DocsTableFilterGroup", className)}>
      <GrStack alignItems="center" gap="8px">
        {filters.map((filter) => {
          return (
            <FilterChip
              key={filter.key}
              filter={filter}
              currentValues={filterValues[filter.key]?.values || []}
              onContextMenuOpen={handleChangeContextMenu}
              onClear={handleClearFilter}
            />
          );
        })}

        {hasActiveFilter && (
          <GrButton size="small" color="grey" variant="text" onClick={handleResetFilters} style={{ width: "max-content" }}>
            <GrTypography type="Content.Subtitle04" color="grey100">
              {MvnMessage(DOCS_COMMON_LANG.CLEAR_FILTERS_NOUN)}
            </GrTypography>
          </GrButton>
        )}
      </GrStack>
      <StyledGrMenu
        anchorElement={contextMenu?.anchor || null}
        position={{ vertical: "bottom", horizontal: "left" }}
        items={contextMenu ? renderContextMenuItems(contextMenu.filter) : []}
        onClose={() => setContextMenu(null)}
      />
    </DocsTableFilterGroupWrapper>
  );
}

const DocsTableFilterGroupWrapper = styled(GrStack)`
  width: 100%;
  height: 24px;
  margin-bottom: 8px;
`;

const StyledGrMenu = styled(GrMenu)`
  .MuiMenuItem-root {
    padding: 0px 4px;
    background-color: ${palette["white"]} !important;
    cursor: default;

    & > div {
      justify-content: flex-start;
    }
  }
`;

const StyledTextField = styled(GrTextField)`
  .MuiInputBase-root {
    padding-right: 4px;
  }
`;
